import {
  Alert,
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Spinner,
  Tag,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { DataTable } from '@gamma/ui/data-table';
import { TextDivider } from '@gamma/ui/display';
import RefreshSharpIcon from '@gamma/ui/icons/RefreshSharpIcon';
import { CrowdStrikeMark } from '@gamma/ui/logos';
import { Modal } from '@gamma/ui/overlay';
import { GraphQLReqStatus } from '@gamma/ui/progress';
import moment from 'moment';
import { useContext, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { Column as TableColumn } from 'react-table';
import { i18n } from '../../../../shared/localization';
import { AuthContext } from '../../../../shared/providers/AuthProvider';
import {
  CrowdStrikeEndpointDevice,
  EntityEdrStatusWithTimestamp,
  useGetEntityEdrStatus,
  usePerformEdrAction,
  usePullEntityEdrStatus,
} from '../../../entities/queries';
import {
  UserHistory,
  NicHistory,
  Detection,
} from '../../../../shared/generated/graphql';
import { InterfaceHistoryModal } from './InterfaceHistoryModal';
import { EntityPanelRow } from './Panels';
import { UserHistoryModal } from './UserHistoryModal';

const { entityEDR } = i18n.pages.detections;
const { headings, edrAction } = entityEDR;
export type CrowdstrikeDetectionDataPanelProps = {
  data: CrowdStrikeEndpointDevice;
  detectionInfo: Detection;
  isRelated?: boolean;
  disableBorders?: boolean;
  actionEnabled: boolean;
};

export const CrowdstrikeDetectionDataPanel = ({
  data,
  detectionInfo,
  isRelated,
  disableBorders = false,
  actionEnabled,
}: CrowdstrikeDetectionDataPanelProps) => {
  const [edrStatus, setEdrStatus] = useState<
    EntityEdrStatusWithTimestamp | undefined
  >();
  const { userRole } = useContext(AuthContext);

  const showToastMessage = useToast();

  const {
    isOpen: interfaceHistoryIsOpen,
    onOpen: interfaceHistoryOnOpen,
    onClose: interfaceHistoryOnClose,
  } = useDisclosure();

  const {
    isOpen: userHistoryIsOpen,
    onOpen: userHistoryOnOpen,
    onClose: userHistoryOnClose,
  } = useDisclosure();

  const {
    isOpen: edrActionIsOpen,
    onOpen: edrActionOnOpen,
    onClose: edrActionOnClose,
  } = useDisclosure();

  const { detection_id } = useParams();

  const { alert_entity, tenant_info } = detectionInfo || {};
  const { entity_name } = alert_entity || {};
  const { tenant_id } = tenant_info || {};

  const {
    entity_id,
    device_id,
    last_updated_timestamp,
    host_info: {
      platform,
      platform_type,
      os,
      sensor_version,
      cloud_account_id,
      cloud_instance_id,
      cloud_provider,
      ad_domain_name,
      ad_organizational_unit_name,
      mac_address,
      external_ip,
      host_name,
    },
    user_history,
    nic_history,
  } = data;

  const { entity_edr_status } = edrStatus || {};

  const entityEdrIsolated =
    entity_edr_status === 'isolated' ||
    entity_edr_status === 'lift_isolation_pending';

  const edrStatusAlert = useMemo(() => {
    if (entity_edr_status) {
      switch (entity_edr_status) {
        case 'normal':
          return {
            colorScheme: 'green',
            statusText: edrAction.normal,
          };
        case 'isolated':
          return {
            colorScheme: 'red',
            statusText: edrAction.isolated,
          };
        case 'isolation_pending':
          return {
            colorScheme: 'yellow',
            statusText: edrAction.pendingIsolation,
          };
        case 'lift_isolation_pending':
          return {
            colorScheme: 'yellow',
            statusText: edrAction.pendingLift,
          };
      }
    }
    return undefined;
  }, [entity_edr_status]);

  const { loading: getEdrStatusLoading, error: getEdrStatusError } =
    useGetEntityEdrStatus({
      skip: !entity_id || !tenant_id,
      fetchPolicy: 'network-only',
      variables: {
        entity_id,
        tenant: tenant_id,
      },
      onCompleted({ getEntityEdrStatus }) {
        const { entity_edr_status, tenant, updated_timestamp } =
          getEntityEdrStatus;
        setEdrStatus({
          tenant,
          entity_edr_status,
          updated_timestamp,
        });
      },
    });

  const [pullEntityEdrStatus, { loading: pullEdrStatusLoading }] =
    usePullEntityEdrStatus({
      variables: {
        entity_id,
        tenant: tenant_id,
      },
      onCompleted({ pullEntityEdrStatus }) {
        const { tenant, entity_edr_status, updated_timestamp } =
          pullEntityEdrStatus;
        setEdrStatus({
          tenant,
          entity_edr_status,
          updated_timestamp,
        });
        showToastMessage({
          status: 'success',
          title: edrAction.toasts.success,
          description: edrAction.toasts.refreshSuccessful,
          isClosable: true,
          position: 'bottom-right',
        });
      },
      onError: () => {
        showToastMessage({
          status: 'error',
          title: edrAction.toasts.error,
          description: edrAction.toasts.refreshFailed,
          isClosable: true,
          position: 'bottom-right',
        });
      },
    });

  const [performEdrAction, { loading: performEdrActionLoading }] =
    usePerformEdrAction({
      variables: {
        tenant: tenant_id ?? '',
        action: !entityEdrIsolated ? 'isolate' : 'lift_isolation',
        edr_device_input: {
          entity_id,
          detection_id: detectionInfo?.detection_id,
        },
      },
      onCompleted(data) {
        edrActionOnClose();
        showToastMessage({
          status: 'success',
          title: edrAction.toasts.success,
          description: edrAction.toasts.requestSubmitted,
          isClosable: true,
          position: 'bottom-right',
        });
        setEdrStatus({
          tenant: data?.performEdrAction?.tenant,
          entity_edr_status: data?.performEdrAction?.entity_edr_status,
          updated_timestamp: data?.performEdrAction?.updated_timestamp,
        });
      },
      onError: () => {
        showToastMessage({
          status: 'error',
          title: edrAction.toasts.error,
          description: edrAction.toasts.requestFailed,
          isClosable: true,
          position: 'bottom-right',
        });
      },
    });

  const dataTableUserColumns: TableColumn<UserHistory>[] = [
    {
      Header: entityEDR.tableHeaders.user,
      accessor: 'username',
      disableSortBy: true,
    },
    {
      Header: entityEDR.tableHeaders.lastLogin,
      accessor: 'obsts',
      Cell: (props) => {
        const value = props.value ?? 0;
        return <>{moment.unix(value).format('MMMM Do, h:mma')}</>;
      },
    },
  ];

  const dataTableInterfaceColumns: readonly TableColumn<NicHistory>[] = [
    {
      Header: entityEDR.tableHeaders.ipAddress,
      accessor: 'ip_address',
      disableSortBy: true,
    },
    {
      Header: entityEDR.macAddress,
      accessor: 'mac_address',
      disableSortBy: true,
    },
    {
      Header: entityEDR.timestamp,
      accessor: 'obsts',
      Cell: (props: { value?: number | null }) => {
        const value = props.value ?? 0;
        return <>{moment.unix(value).format('MMMM Do, h:mma')}</>;
      },
    },
  ];

  const handleEdrActionEvent = () => {
    if (entity_id && tenant_id && detectionInfo?.detection_id) {
      performEdrAction();
    }
  };
  return (
    <>
      <HStack>
        <CrowdStrikeMark />
        <Text textStyle="h5">Crowdstrike</Text>
      </HStack>
      {actionEnabled &&
        (getEdrStatusError || getEdrStatusLoading ? (
          <GraphQLReqStatus
            loading={getEdrStatusLoading}
            error={getEdrStatusError}
          />
        ) : (
          <Flex w="100%" data-testid="entity-edr-status">
            <EntityPanelRow heading={edrAction.entityStatus}>
              <HStack justifyContent="space-between">
                <HStack>
                  {edrStatusAlert && (
                    <Tag
                      size="md"
                      variant="solid"
                      colorScheme={edrStatusAlert?.colorScheme}
                    >
                      {edrStatusAlert?.statusText}
                    </Tag>
                  )}
                  <IconButton
                    data-testid="pull-entity-edr-status-button"
                    aria-label={edrAction.refreshEntityStatus}
                    onClick={() => pullEntityEdrStatus()}
                    isDisabled={
                      getEdrStatusLoading ||
                      pullEdrStatusLoading ||
                      userRole === 'viewer'
                    }
                    icon={
                      getEdrStatusLoading || pullEdrStatusLoading ? (
                        <Box w="20px" h="20px">
                          <Spinner size="xs" color="white" />
                        </Box>
                      ) : (
                        <RefreshSharpIcon />
                      )
                    }
                  />
                </HStack>
                <Tooltip
                  w="120px"
                  placement="top"
                  label={
                    userRole !== 'admin'
                      ? edrAction.requestPermissions
                      : undefined
                  }
                >
                  <Button
                    size="xs"
                    variant="solid"
                    colorScheme="gray"
                    onClick={edrActionOnOpen}
                    data-testid="edr-action-open-button"
                    isDisabled={
                      userRole !== 'admin' ||
                      getEdrStatusLoading ||
                      pullEdrStatusLoading ||
                      entity_edr_status === 'isolation_pending' ||
                      entity_edr_status === 'lift_isolation_pending'
                    }
                  >
                    {entityEdrIsolated
                      ? edrAction.liftIsolation
                      : edrAction.isolateEntity}
                  </Button>
                </Tooltip>
              </HStack>
            </EntityPanelRow>
          </Flex>
        ))}
      <EntityPanelRow heading={entityEDR.timestamp}>
        {last_updated_timestamp &&
          moment.unix(last_updated_timestamp).format('MMMM D, YYYY h:mma')}
      </EntityPanelRow>
      <EntityPanelRow heading={entityEDR.macAddress}>
        {mac_address}
      </EntityPanelRow>
      <EntityPanelRow heading={entityEDR.hostName}>{host_name}</EntityPanelRow>
      <EntityPanelRow heading={entityEDR.platform}>{platform}</EntityPanelRow>
      <EntityPanelRow heading={entityEDR.platformType}>
        {platform_type}
      </EntityPanelRow>
      <EntityPanelRow heading={entityEDR.os}>{os}</EntityPanelRow>
      <EntityPanelRow heading={entityEDR.externalIp}>
        {external_ip}
      </EntityPanelRow>
      {!isRelated && detection_id && (
        <>
          <EntityPanelRow
            heading={entityEDR.deviceId}
            disableBorders={disableBorders}
          >
            {device_id}
          </EntityPanelRow>
          <EntityPanelRow heading={entityEDR.sensorVersion}>
            {sensor_version}
          </EntityPanelRow>
          <EntityPanelRow heading={entityEDR.cloudAccountId}>
            {cloud_account_id}
          </EntityPanelRow>
          <EntityPanelRow heading={entityEDR.cloudInstanceId}>
            {cloud_instance_id}
          </EntityPanelRow>
          <EntityPanelRow heading={entityEDR.cloudProvider}>
            {cloud_provider}
          </EntityPanelRow>
          <EntityPanelRow heading={entityEDR.adDomainName}>
            {ad_domain_name}
          </EntityPanelRow>
          <EntityPanelRow heading={entityEDR.adOrganizationalUnitName}>
            {ad_organizational_unit_name}
          </EntityPanelRow>
          {nic_history && nic_history?.nic_histories?.length > 0 && (
            <>
              <TextDivider>{headings.interfaceHistory}</TextDivider>
              <Box w={disableBorders ? '365px' : '100%'}>
                <DataTable
                  isLined
                  size="sm"
                  isSortable={false}
                  isPaginable={false}
                  data={nic_history?.nic_histories as NicHistory[]}
                  columns={
                    dataTableInterfaceColumns as readonly TableColumn<NicHistory>[]
                  }
                />
              </Box>
              {nic_history?.total_items > 5 && (
                <Button
                  w="100%"
                  size="xs"
                  variant="solid"
                  colorScheme="gray"
                  data-testid="open-history-button"
                  onClick={() => {
                    interfaceHistoryOnOpen();
                  }}
                >
                  {entityEDR.viewRemainingInterfaceHistory}
                </Button>
              )}
            </>
          )}
          {user_history && user_history?.user_histories?.length > 0 && (
            <>
              <TextDivider>{headings.userHistory}</TextDivider>
              <Box w={disableBorders ? '365px' : '100%'}>
                <DataTable
                  isLined
                  size="sm"
                  isSortable={false}
                  isPaginable={false}
                  data={user_history?.user_histories.slice(5)}
                  columns={dataTableUserColumns}
                />
              </Box>
              {user_history?.total_items > 5 && (
                <Button
                  w="100%"
                  size="xs"
                  variant="solid"
                  colorScheme="gray"
                  data-testid="open-user-history-button"
                  onClick={() => {
                    userHistoryOnOpen();
                  }}
                >
                  {entityEDR.viewRemainingUserHistory}
                </Button>
              )}
            </>
          )}
        </>
      )}
      <UserHistoryModal
        isOpen={userHistoryIsOpen}
        onClose={userHistoryOnClose}
        detectionInfo={detectionInfo as Detection}
        edrEntityId={entity_id || ''}
      />
      <InterfaceHistoryModal
        isOpen={interfaceHistoryIsOpen}
        onClose={interfaceHistoryOnClose}
        detectionInfo={detectionInfo as Detection}
        edrEntityId={entity_id || ''}
      />

      <Modal
        isOpen={edrActionIsOpen}
        onClose={edrActionOnClose}
        title={`${
          entityEdrIsolated
            ? edrAction.liftEntityIsolation
            : edrAction.isolateEntity
        } in CrowdStrike`}
        body={
          entityEdrIsolated ? (
            <Text>
              <Text as="span" textStyle="body-md-bold">
                {entity_name}
              </Text>{' '}
              {edrAction.willBeAdded}
            </Text>
          ) : (
            <Alert variant="subtle" status="warning">
              <Text>
                <Text as="span" textStyle="body-md-bold">
                  {entity_name}
                </Text>{' '}
                {edrAction.willBeIsolated}
              </Text>
            </Alert>
          )
        }
        footer={
          <HStack>
            <Button
              variant="solid"
              colorScheme="gray"
              onClick={edrActionOnClose}
              data-testid="cancel-button"
            >
              {edrAction.cancel}
            </Button>
            <Button
              variant="solid"
              colorScheme="blue"
              onClick={handleEdrActionEvent}
              isLoading={performEdrActionLoading}
              isDisabled={performEdrActionLoading}
              data-testid="edr-action-event-button"
            >
              {entity_edr_status === 'normal' ||
              entity_edr_status === 'isolation_pending'
                ? edrAction.isolateEntity
                : edrAction.liftIsolation}
            </Button>
          </HStack>
        }
      />
    </>
  );
};
